import type {
  RouteLocation,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";
import type { Acl, User } from "vue-simple-acl/types/acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import {
  PATH_SUPER_ADMINISTRATION,
  PATH_SUPER_ADMINISTRATION_SETTINGS,
  PATH_SUPER_ADMINISTRATION_SETTINGS_INFORMATION,
  PATH_SUPER_ADMINISTRATION_SETTINGS_KEYSTORE,
  PATH_SUPER_ADMINISTRATION_SETTINGS_KEYSTORE_UUID,
  PATH_SUPER_ADMINISTRATION_SETTINGS_LOGS,
  PATH_SUPER_ADMINISTRATION_SETTINGS_PKI,
  PATH_SUPER_ADMINISTRATION_SETTINGS_PKI_ALIAS,
  PATH_SUPER_ADMINISTRATION_SETTINGS_TRUSTSTORE,
  PATH_SUPER_ADMINISTRATION_SETTINGS_TRUSTSTORE_ALIAS,
  PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT,
  PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT_PROFILES,
  PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT_PROFILES_ID,
  PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_ADMINISTRATION,
  PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_OPERATOR,
  PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT_USERS,
  PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT_USERS_LOGIN,
} from "~/modules/superAdministration/constants";
import { router } from "~/plugins/router";

const routes: RouteRecordRaw[] = [
  {
    path: PATH_SUPER_ADMINISTRATION,
    component: () =>
      import("~/modules/superAdministration/views/SuperAdministrationPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_ORG),
    },
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS,
    redirect: PATH_SUPER_ADMINISTRATION_SETTINGS_INFORMATION,
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS_INFORMATION,
    component: () =>
      import("~/modules/superAdministration/views/SuperAdministrationSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_ORG),
    },
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT,
    redirect: PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT_USERS,
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT_USERS,
    component: () =>
      import("~/modules/superAdministration/views/SuperAdministrationSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_USER),
    },
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT_USERS_LOGIN,
    component: () =>
      import("~/modules/superAdministration/views/SuperAdministrationSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_WRITE_USER),
    },
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT_PROFILES,
    component: () =>
      import("~/modules/superAdministration/views/SuperAdministrationSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_USER),
    },
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT_PROFILES_ID,
    redirect: (to: RouteLocation) =>
      router.resolve({
        name: PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_ADMINISTRATION,
        params: { profileId: to.params.profileId },
      }),
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_ADMINISTRATION,
    component: () =>
      import("~/modules/superAdministration/views/SuperAdministrationSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_USER),
    },
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS_USER_MANAGEMENT_PROFILES_ID_PERMISSIONS_OPERATOR,
    component: () =>
      import("~/modules/superAdministration/views/SuperAdministrationSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_USER),
    },
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS_LOGS,
    component: () =>
      import("~/modules/superAdministration/views/SuperAdministrationSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
    },
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS_KEYSTORE,
    component: () =>
      import("~/modules/superAdministration/views/SuperAdministrationSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_CERTIFICATE),
    },
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS_KEYSTORE_UUID,
    component: () =>
      import("~/modules/superAdministration/views/SuperAdministrationSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_CERTIFICATE),
    },
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS_TRUSTSTORE,
    component: () =>
      import("~/modules/superAdministration/views/SuperAdministrationSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_CERTIFICATE),
    },
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS_TRUSTSTORE_ALIAS,
    component: () =>
      import("~/modules/superAdministration/views/SuperAdministrationSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_CERTIFICATE),
    },
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS_PKI,
    component: () =>
      import("~/modules/superAdministration/views/SuperAdministrationSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_FEATURE),
    },
  },
  {
    path: PATH_SUPER_ADMINISTRATION_SETTINGS_PKI_ALIAS,
    component: () =>
      import("~/modules/superAdministration/views/SuperAdministrationSettingsPage.vue"),
    meta: {
      layout: "AdministrationLayout",
      can: (
        _to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        can: Acl<User>["can"],
      ) => can(ACLRoles.CAN_READ_FEATURE),
    },
  },
];

export default routes;
